import { Component } from '@theme/component';

/**
 * HH · Cuenta regresiva.
 *
 * Lee las promociones que deja la sección (JSON con inicio y fin en segundos) y cada segundo:
 * - Con promociones activas: estado «En curso», cuenta hasta el fin de la que termina primero
 *   y muestra las marcas con descuento de todas las activas.
 * - Si no: la próxima en empezar dentro de la ventana de días («Muy pronto»), con la fecha.
 * - Si no hay ninguna: oculta la sección.
 * El cambio de estado ocurre a la hora exacta, aunque la página se haya cargado antes. Los
 * números cambian cada segundo pero no se anuncian; para lectores de pantalla hay una frase
 * con la fecha que solo cambia con el estado.
 *
 * @typedef {{ handle: string, name: string, url: string }} Brand
 * @typedef {{ id: string, name: string, message: string, summary: string, code: string,
 *   start: number, end: number, all: boolean, link: string, brands: Brand[] }} Promo
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data
 * @property {HTMLScriptElement} labels
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 * @property {HTMLElement} [chip]
 * @property {HTMLElement} [name]
 * @property {HTMLElement} [clock]
 * @property {HTMLElement} [clockLabel]
 * @property {HTMLElement} [spoken]
 * @property {HTMLElement} [message]
 * @property {HTMLElement} [date]
 * @property {HTMLElement} [brands]
 * @property {HTMLElement} [brandList]
 * @property {HTMLButtonElement} [codeButton]
 * @property {HTMLElement} [code]
 * @property {HTMLAnchorElement} [link]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<Refs>}
 */
export class HhCuentaRegresiva extends Component {
  requiredRefs = ['data', 'labels', 'days', 'hours', 'minutes', 'seconds'];

  /** @type {Promo[]} */
  #promos = [];
  /** @type {Record<string, string>} */
  #labels = {};
  #timer = 0;
  #renderedKey = '';
  /** @type {Promo | null} */
  #featured = null;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#timer);
  }

  /** Copia el código de la promoción y avisa. */
  async copyCode() {
    const code = this.#featured?.code;
    const { status } = this.refs;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      this.#announce(this.#labels.copied ?? '');
      this.toggleAttribute('data-copied', true);
      window.setTimeout(() => this.removeAttribute('data-copied'), 2000);
    } catch {
      if (status) this.#announce((this.#labels.copyFailed ?? '').replace('[code]', code));
    }
  }

  #start() {
    window.clearInterval(this.#timer);
    this.#renderedKey = '';
    try {
      this.#promos = JSON.parse(this.refs.data.textContent || '[]');
      this.#labels = JSON.parse(this.refs.labels.textContent || '{}');
    } catch {
      this.#promos = [];
    }
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  #tick() {
    const now = Date.now() / 1000;
    const windowSeconds = Number(this.dataset.window) || 0;

    const active = this.#promos.filter((promo) => promo.start <= now && (!promo.end || now < promo.end));
    const upcoming = this.#promos
      .filter((promo) => promo.start > now && promo.start - now <= windowSeconds)
      .sort((a, b) => a.start - b.start);

    /** @type {Promo | null} */
    let featured = null;
    let state = '';
    if (active.length) {
      state = 'active';
      const withEnd = active.filter((promo) => promo.end).sort((a, b) => a.end - b.end);
      featured = withEnd[0] ?? active[0] ?? null;
    } else if (upcoming.length) {
      state = 'upcoming';
      featured = upcoming[0] ?? null;
    }

    if (!featured) {
      this.hidden = true;
      this.dataset.state = 'none';
      window.clearInterval(this.#timer);
      // Una promoción que empieza más adelante: vuelve a revisar cada minuto.
      if (this.#promos.some((promo) => promo.start > now)) {
        this.#timer = window.setInterval(() => this.#tick(), 60000);
      }
      return;
    }

    if (this.hidden) {
      this.hidden = false;
      window.clearInterval(this.#timer);
      this.#timer = window.setInterval(() => this.#tick(), 1000);
    }

    const key = `${state}:${featured.id}:${active.map((promo) => promo.id).join(',')}`;
    if (key !== this.#renderedKey) {
      this.#renderedKey = key;
      this.#featured = featured;
      this.#render(state, featured, active);
    }

    const target = state === 'active' ? featured.end : featured.start;
    if (target) this.#renderClock(Math.max(0, Math.floor(target - now)));
  }

  /**
   * @param {string} state
   * @param {Promo} featured
   * @param {Promo[]} active
   */
  #render(state, featured, active) {
    const { chip, name, clock, clockLabel, spoken, message, date, brands, brandList, codeButton, code, link } =
      this.refs;
    const isActive = state === 'active';
    const labels = this.#labels;

    this.dataset.state = state;
    if (chip) chip.textContent = (isActive ? labels.chipActive : labels.chipUpcoming) ?? '';
    if (name) name.textContent = featured.name;
    if (clockLabel) clockLabel.textContent = (isActive ? labels.clockActive : labels.clockUpcoming) ?? '';

    const hasClock = isActive ? Boolean(featured.end) : true;
    if (clock) clock.hidden = !hasClock;

    if (message) {
      const text = featured.message || featured.summary || (isActive && !featured.end ? labels.noEnd ?? '' : '');
      message.textContent = text;
      message.hidden = !text;
    }

    const when = isActive ? featured.end : featured.start;
    const formatted = when ? this.#formatDate(when) : '';
    if (date) {
      date.hidden = isActive || !formatted;
      date.textContent = (labels.startsOn ?? '').replace('[fecha]', formatted);
    }
    if (spoken) {
      const template = isActive ? labels.endsOn : labels.startsOn;
      spoken.textContent = formatted ? (template ?? '').replace('[fecha]', formatted) : '';
    }

    if (brands && brandList) {
      brandList.replaceChildren();
      const storeWide = active.some((promo) => promo.all);
      const list = this.#uniqueBrands(active);
      brands.hidden = !isActive || (!storeWide && list.length === 0);
      if (isActive && storeWide) {
        brandList.textContent = labels.storeWide ?? '';
      } else if (isActive) {
        this.#renderBrands(brandList, list);
      }
    }

    if (codeButton && code) {
      codeButton.hidden = !isActive || !featured.code;
      code.textContent = featured.code;
    }
    if (link) {
      link.hidden = !isActive || Boolean(featured.code);
      if (featured.link) link.href = featured.link;
    }
  }

  /** @param {number} remaining - Segundos. */
  #renderClock(remaining) {
    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    const { days: daysRef, hours: hoursRef, minutes: minutesRef, seconds: secondsRef } = this.refs;

    setNumber(daysRef, days);
    setNumber(hoursRef, hours);
    setNumber(minutesRef, minutes);
    setNumber(secondsRef, seconds);

    // Sin días por delante, el bloque de días se esconde.
    const daysUnit = daysRef.closest('.hh-cr__unit');
    const daysSeparator = this.querySelector('.hh-cr__sep--days');
    if (daysUnit instanceof HTMLElement) daysUnit.hidden = days === 0;
    if (daysSeparator instanceof HTMLElement) daysSeparator.hidden = days === 0;
  }

  /**
   * @param {Promo[]} promos
   * @returns {Brand[]}
   */
  #uniqueBrands(promos) {
    /** @type {Map<string, Brand>} */
    const seen = new Map();
    for (const promo of promos) {
      for (const brand of promo.brands) {
        if (!seen.has(brand.handle)) seen.set(brand.handle, brand);
      }
    }
    return [...seen.values()];
  }

  /**
   * «Anua, COSRX y Skin1004», con enlace a cada marca y «+N más» si son muchas.
   * @param {HTMLElement} container
   * @param {Brand[]} list
   */
  #renderBrands(container, list) {
    const max = Number(this.dataset.maxBrands) || list.length;
    const shown = list.slice(0, max);
    const extra = list.length - shown.length;
    const labels = this.#labels;

    shown.forEach((brand, index) => {
      if (index > 0) {
        const isLast = index === shown.length - 1 && extra === 0;
        container.append(isLast ? ` ${labels.and ?? 'y'} ` : ', ');
      }
      const item = document.createElement(brand.url ? 'a' : 'span');
      item.className = 'hh-cr__brand';
      item.textContent = brand.name;
      if (item instanceof HTMLAnchorElement) item.href = brand.url;
      container.append(item);
    });

    if (extra > 0) {
      container.append(` ${labels.and ?? 'y'} `);
      const more = document.createElement('span');
      more.className = 'hh-cr__brand hh-cr__brand--more';
      more.textContent = (labels.moreBrands ?? '').replace('[count]', String(extra));
      container.append(more);
    }
  }

  /** @param {number} seconds */
  #formatDate(seconds) {
    const lang = document.documentElement.lang || 'es';
    return new Intl.DateTimeFormat(lang, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(seconds * 1000));
  }

  /** @param {string} text */
  #announce(text) {
    const { status } = this.refs;
    if (!status) return;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = text;
    });
  }
}

/**
 * @param {HTMLElement} element
 * @param {number} value
 */
function setNumber(element, value) {
  const text = String(value).padStart(2, '0');
  if (element.textContent !== text) element.textContent = text;
}

if (!customElements.get('hh-cuenta-regresiva')) {
  customElements.define('hh-cuenta-regresiva', HhCuentaRegresiva);
}
